export type EnumObject = Record<string, number | string>;

export function getNamesAndValuesOfEnum<E extends EnumObject>(e: E) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return getNamesOfEnum(e).map((n) => ({ name: n, value: e[n] as E[keyof E] }));
}

export function getNamesOfEnum<E extends EnumObject>(e: E) {
    return Object.keys(e);
}

export function getTypedNamesOfEnum<E extends EnumObject>(e: E): Array<keyof E> {
    return getNamesOfEnum(e).filter((key) => isEnumKey(key, e));
}

export function getValuesOfEnum<E extends EnumObject>(e: E) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return Object.keys(e).map((k) => e[k] as E[keyof E]);
}

export function isEnumValue<E extends EnumObject>(value: unknown, enumObject: E): value is E[keyof E] {
    return (typeof value === "string" || typeof value === "number") && Object.values(enumObject).includes(value);
}

export function safeCastEnumValue<E extends EnumObject>(value: unknown, enumObject: E): E[keyof E] | undefined {
    if (isEnumValue(value, enumObject)) {
        return value;
    }
    return undefined;
}

export function isEnumKey<E extends EnumObject>(key: unknown, enumObject: E): key is keyof E {
    return typeof key === "string" && isNaN(Number(key)) && Object.keys(enumObject).includes(key);
}

export function safeCastEnumKey<E extends EnumObject>(key: unknown, enumObject: E): keyof E | undefined {
    if (isEnumKey(key, enumObject)) {
        return key;
    }
    return undefined;
}

export function getEnumValueFromKey<E extends EnumObject>(key: unknown, enumObject: E): E[keyof E] | undefined {
    if (isEnumKey(key, enumObject)) {
        return enumObject[key];
    }
    return undefined;
}

export function getEnumNameFromValue<E extends EnumObject>(value: number | string, enumObject: E): string | undefined {
    for (const key of Object.keys(enumObject)) {
        if (enumObject[key] === value) {
            return key;
        }
    }
    return undefined;
}
